import React, { useEffect, useState } from "react";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import TimeRangeSelector from "../../components/helpers/TimeRangeSelector";
import { parseRequest } from "../../components/helpers/response_parser";
import { useTranslation } from "react-i18next";

import StandardScreen, {
  ScreenContent,
} from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import FormField from "../../components/forms/FormField";
import SelectInput from "../../components/forms/SelectInput";
import TextInput from "../../components/forms/TextInput";
import Switch from "../../components/ui/Switch";

import {
  TableShell,
  Table,
  Th,
  Td,
} from "../../components/data/Table";

const narrowShellStyle = {
  width: "100%",
  margin: "0 auto",
};

export default function AdminLogReport() {
  const { t } = useTranslation();

  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [timeRange, setTimeRange] = useState({
    start: "",
    end: "",
  });
  const [notesFilter, setNotesFilter] = useState("");
  const [showResponse, setShowResponse] = useState(false);

  const fetchLogs = async () => {
    try {
      axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${localStorage.getItem("token")}`;

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/detailed-logs`
      );

      setLogs(res.data);
    } catch (error) {
      console.error("Error fetching transfer logs:", error);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    const filtered = logs.filter((log) => {
      const created = new Date(log.created_at);
      const start = new Date(timeRange.start);
      const end = new Date(timeRange.end);

      const timeMatch =
        created >= start &&
        created <= end;

      const statusMatch =
        statusFilter === "all" ||
        log.status === statusFilter;

      const search = notesFilter
        .trim()
        .toLowerCase();

      const notesText = (
        log.notes || ""
      ).toLowerCase();

      const responseText = (
        log.response || ""
      ).toLowerCase();

      const notesMatch =
        search === "" ||
        notesText.includes(search) ||
        responseText.includes(search);

      return (
        statusMatch &&
        timeMatch &&
        notesMatch
      );
    });

    setFilteredLogs(filtered);
  }, [
    logs,
    statusFilter,
    timeRange,
    notesFilter,
  ]);

  return (
    <StandardScreen
      topNav={
        <TopNav
          title={t("admin_log_report.title")}
          mode="main"
        />
      }
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">

            {/* FILTERS */}
            <Card title={t("admin_log_report.title")}>
              <Stack gap="3">

                <FormField>
                  <div>
                    <TimeRangeSelector
                      onRangeChange={setTimeRange}
                    />
                  </div>
                </FormField>

                <FormField
                  label={t("status_dropdown.label")}
                >
                  <SelectInput
                    value={statusFilter}
                    onChange={(e) =>
                      setStatusFilter(e.target.value)
                    }
                  >
                    <option value="all">
                      {t("status_dropdown.values.all")}
                    </option>

                    <option value="approved">
                      {t("status_dropdown.values.approved")}
                    </option>

                    <option value="cancelled">
                      {t("status_dropdown.values.cancelled")}
                    </option>

                    <option value="pending">
                      {t("status_dropdown.values.pending")}
                    </option>
                  </SelectInput>
                </FormField>

                <FormField
                  label={t(
                    "admin_log_report.fields.notes_filter.label"
                  )}
                >
                  <TextInput
                    type="text"
                    value={notesFilter}
                    onChange={(e) =>
                      setNotesFilter(e.target.value)
                    }
                    placeholder={t(
                      "admin_log_report.fields.notes_filter.placeholder"
                    )}
                  />
                </FormField>

                <FormField
                  label={t(
                    "admin_log_report.fields.show_response.label"
                  )}
                >
                  <Switch
                    checked={showResponse}
                    onChange={setShowResponse}
                  />
                </FormField>

              </Stack>
            </Card>

            {/* LOG TABLE */}
            <Card>
              <TableShell maxHeight="65vh">
                <Table>
                  <thead>
                    <tr>

                      <Th columnIndex={0} sticky>{t("admin_log_report.table.treq_id")}</Th>
                      <Th columnIndex={1} sticky>{t("admin_log_report.table.from_fullname")}</Th>
                      <Th>{t("admin_log_report.table.to_fullname")}</Th>
                      <Th>{t("admin_log_report.table.from_wallet_id")}</Th>
                      <Th>{t("admin_log_report.table.created_at")}</Th>
                      {/* NEW REQUEST COLUMN */}
                      <Th>{t("admin_log_report.table.request")}</Th>
                      <Th>{t("admin_log_report.table.status")}</Th>
                      {showResponse && (<Th>{t("admin_log_report.table.response")}</Th>)}

                    </tr>
                  </thead>

                  <tbody>
                    {filteredLogs.map((log) => (
                      <tr key={log.treq_id}>

                        <Td columnIndex={0} sticky>{log.treq_id}</Td>
                        <Td columnIndex={1} sticky>{log.from_fullname}</Td>
                        <Td>{log.to_fullname}</Td>
                        <Td>{log.from_wallet_id}</Td>
                        <Td>{new Date(log.created_at).toLocaleString("en-GB", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).replace(",", "")}</Td>
                        {/* NEW REQUEST VALUE */}
                        <Td>{parseRequest(log.response)}</Td>
                        <Td>{t(`status_dropdown.values.${log.status}`)}</Td>
                        {showResponse && (<Td>{log.response}</Td>)}

                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableShell>
            </Card>

          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}